import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import { cached, tracked } from '@glimmer/tracking';

import type { DateRangeInterface, DSSetup, ImageCropData, RecordData, Service } from 'webapp-types';
import type {
	AiSummaryContextLevel,
	AiSummaryLength,
	BlockFontSize,
	ChartLegendPosition,
	OrderDirection,
	SortFieldType,
	TableColumnType,
} from 'webapp/enums/enums';
import {
	BillingFeature,
	BlockDiscriminator,
	BlockVisualization,
	DataPerformanceSummaryTrend,
	ModelName,
} from 'webapp/enums/enums';
import { getGradient } from 'webapp/utils/color';
import { uniq } from 'webapp/utils/common';

import { AiSummaryContentStatus } from 'webapp/components/block/content/ai-summary';
import BlockPopupPreviewComponent from 'webapp/components/block/popup-preview';

import type { BoardPreviewRecord } from 'webapp/models';
import type { Board, Metric } from 'webapp/models-custom';
import BlockSetup from 'webapp/models-custom/block-setup';
import type { DirectProps } from 'webapp/models-custom/buffer/buffered-model';
import BufferedModel from 'webapp/models-custom/buffer/buffered-model';
import type DataSelect from 'webapp/models-custom/data-select';
import type { GridPosition } from 'webapp/models-custom/datawall-grid';
import DatawallGrid from 'webapp/models-custom/datawall-grid';
import DateRange from 'webapp/models-custom/date-range';
import type Source from 'webapp/models-custom/source';
import type StatusObject from 'webapp/models-custom/status-object';
import type { ColorSetting, YAxisScale } from 'webapp/models/block';
import type BlockRecord from 'webapp/models/block';
import type BlockPreviewRecord from 'webapp/models/block-preview';
import type BoardRecord from 'webapp/models/board';
import type SampleRecord from 'webapp/models/sample';

import { Severity } from 'webapp/services/logger';

export type DSMap = Record<string, number>; // used for data stories { <order>:<dsId> }

export default class Block extends BufferedModel<BlockRecord, RecordData.Block.Serialized> {
	@service('request') declare requestService: Service.Request;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('billing') declare billingService: Service.Billing;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('logger') declare loggerService: Service.Logger;
	@service('constants') declare constantsService: Service.Constants;
	@service('account') declare accountService: Service.Account;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/board-previews')
	declare mmBoardPreviewsService: Service.ModelManager.BoardPreviews;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('model-manager/samples') declare mmSamplesService: Service.ModelManager.Samples;
	@service('model-manager/report-slide-fragments')
	declare mmReportSlideFragmentsService: Service.ModelManager.ReportSlideFragments;
	@service('model-manager/custom-queries')
	declare mmCustomQueriesService: Service.ModelManager.CustomQueries;
	@service('model-manager/scripts') declare mmScriptsService: Service.ModelManager.Scripts;
	@service('metric-details') declare metricDetailsService: Service.MetricDetails;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('popup') declare popupService: Service.Popup;

	@tracked themeId?: number;

	// ------- props from / to ED record ------

	get id(): string | null {
		return this.getBuffered('id');
	}

	get name(): string {
		return this.getBuffered('name');
	}

	set name(value: string) {
		this.setBuffered('name', value);
	}

	get type(): number {
		return this.getBuffered('type');
	}

	set type(value: number) {
		this.setBuffered('type', value);
	}

	get order(): number {
		return this.getBuffered('order');
	}

	set order(value: number) {
		this.setBuffered('order', value);
	}

	get discriminator(): BlockDiscriminator | null {
		return this.getBuffered('discriminator');
	}

	set discriminator(value: BlockDiscriminator) {
		this.setBuffered('discriminator', value);
	}

	get localId(): number {
		return this.getBuffered('localId');
	}

	set localId(value: number) {
		this.setBuffered('localId', value);
	}

	get boardId(): number {
		return this.getBuffered('boardId');
	}

	set boardId(value: number) {
		this.setBuffered('boardId', value);
	}

	get color(): string {
		return this.getBuffered('color');
	}

	set color(value: string) {
		this.setBuffered('color', value);
	}

	get originateFrom(): number | null {
		return this.model.originateFrom;
	}

	get dateRange(): DateRangeInterface.FormulaInterface {
		return this.getBuffered('dateRange');
	}

	set dateRange(value: DateRangeInterface.FormulaInterface) {
		this.setBuffered('dateRange', value);
	}

	/* ---- settings ---- */

	get visualization(): BlockVisualization {
		return this.getBuffered('visualization');
	}

	set visualization(value: BlockVisualization) {
		this.setBuffered('visualization', value);
	}

	get icon(): number | null {
		return this.getBuffered('icon');
	}

	set icon(value: number | null) {
		this.setBuffered('icon', value);
	}

	get itemsInLine(): number | null {
		return this.getBuffered('itemsInLine');
	}

	set itemsInLine(value: number | null) {
		this.setBuffered('itemsInLine', value);
	}

	get sortOrder(): OrderDirection | null {
		return this.getBuffered('sortOrder');
	}

	set sortOrder(value: OrderDirection | null) {
		this.setBuffered('sortOrder', value);
	}

	get sortField(): SortFieldType | null {
		return this.getBuffered('sortField');
	}

	set sortField(value: SortFieldType | null) {
		this.setBuffered('sortField', value);
	}

	get recordLimit(): number | null {
		return this.getBuffered('recordLimit');
	}

	set recordLimit(value: number | null) {
		this.setBuffered('recordLimit', value);
	}

	get text(): string {
		return this.getBuffered('text');
	}

	set text(value: string) {
		this.setBuffered('text', value);
	}

	get legendPosition(): ChartLegendPosition {
		return this.getBuffered('legendPosition');
	}

	set legendPosition(value: ChartLegendPosition) {
		this.setBuffered('legendPosition', value);
	}

	get mediaEnabled(): boolean {
		return this.getBuffered('mediaEnabled');
	}

	set mediaEnabled(value: boolean) {
		this.setBuffered('mediaEnabled', value);
	}

	get hasRowNumbers(): boolean {
		return this.getBuffered('hasRowNumbers');
	}

	set hasRowNumbers(value: boolean) {
		this.setBuffered('hasRowNumbers', value);
	}

	get tableColumnType(): TableColumnType | null {
		return this.getBuffered('tableColumnType');
	}

	set tableColumnType(value: TableColumnType | null) {
		this.setBuffered('tableColumnType', value);
	}

	get tableColumnInvert(): boolean {
		return this.getBuffered('tableColumnInvert');
	}

	set tableColumnInvert(value: boolean) {
		this.setBuffered('tableColumnInvert', value);
	}

	get colors(): ColorSetting[] {
		return this.getBuffered('colors') || [];
	}

	set colors(value: ColorSetting[]) {
		this.setBuffered('colors', value);
	}

	get omitTimestamp(): boolean {
		return this.getBuffered('omitTimestamp');
	}

	set omitTimestamp(value: boolean) {
		this.setBuffered('omitTimestamp', value);
	}

	get imageUrl(): string {
		return this.getBuffered('imageUrl');
	}

	set imageUrl(value: string) {
		this.setBuffered('imageUrl', value);
	}

	get imageOriginalUrl(): string {
		return this.getBuffered('imageOriginalUrl');
	}

	set imageOriginalUrl(value: string) {
		this.setBuffered('imageOriginalUrl', value);
	}

	get imageCrop(): ImageCropData | null {
		return this.getBuffered('imageCrop');
	}

	set imageCrop(value: ImageCropData | null) {
		this.setBuffered('imageCrop', value);
	}

	get externalUrl(): string {
		return this.getBuffered('externalUrl');
	}

	set externalUrl(value: string) {
		this.setBuffered('externalUrl', value);
	}

	get firstSliceConversion(): boolean {
		return this.getBuffered('firstSliceConversion');
	}

	set firstSliceConversion(value: boolean) {
		this.setBuffered('firstSliceConversion', value);
	}

	get hideStepConversion(): boolean {
		return this.getBuffered('hideStepConversion');
	}

	set hideStepConversion(value: boolean) {
		this.setBuffered('hideStepConversion', value);
	}

	get hideOverallConversionRate(): boolean {
		return this.getBuffered('hideOverallConversionRate');
	}

	set hideOverallConversionRate(value: boolean) {
		this.setBuffered('hideOverallConversionRate', value);
	}

	get onlyMainMetricDimensions(): boolean {
		return this.getBuffered('onlyMainMetricDimensions');
	}

	set onlyMainMetricDimensions(value: boolean) {
		this.setBuffered('onlyMainMetricDimensions', value);
	}

	get dsMap(): DSMap {
		return this.getBuffered('dsMap') || {};
	}

	set dsMap(value: DSMap | null) {
		this.setBuffered('dsMap', value);
	}

	get showOther(): boolean {
		return this.getBuffered('showOther');
	}

	set showOther(value: boolean) {
		this.setBuffered('showOther', value);
	}

	get yAxisScale(): YAxisScale[] {
		return this.getBuffered('yAxisScale');
	}

	set yAxisScale(value: YAxisScale[]) {
		this.setBuffered('yAxisScale', value);
	}

	get isSpline(): boolean {
		return this.getBuffered('isSpline');
	}

	set isSpline(value: boolean) {
		this.setBuffered('isSpline', value);
	}

	get showTotals(): boolean {
		return this.getBuffered('showTotals');
	}

	set showTotals(value: boolean) {
		this.setBuffered('showTotals', value);
	}

	get labelHeader(): string {
		return this.getBuffered('labelHeader');
	}

	set labelHeader(value: string) {
		this.setBuffered('labelHeader', value);
	}

	get fontSize(): BlockFontSize {
		return this.getBuffered('fontSize');
	}

	set fontSize(value: BlockFontSize) {
		this.setBuffered('fontSize', value);
	}

	get posX(): number {
		return this.getBuffered('posX');
	}

	set posX(value: number) {
		this.setBuffered('posX', value);
	}

	get posY(): number {
		return this.getBuffered('posY');
	}

	set posY(value: number) {
		this.setBuffered('posY', value);
	}

	get width(): number {
		return this.getBuffered('width');
	}

	set width(value: number) {
		this.setBuffered('width', value);
	}

	get height(): number {
		return this.getBuffered('height');
	}

	set height(value: number) {
		this.setBuffered('height', value);
	}

	get aiSummaryShowTrend(): boolean {
		return this.getBuffered('aiSummaryShowTrend');
	}

	set aiSummaryShowTrend(value: boolean) {
		this.setBuffered('aiSummaryShowTrend', value);
	}

	get aiSummaryLanguage(): string {
		return this.getBuffered('aiSummaryLanguage');
	}

	set aiSummaryLanguage(value: string) {
		this.setBuffered('aiSummaryLanguage', value);
	}

	get aiSummaryContextLevelType(): AiSummaryContextLevel | null {
		return this.getBuffered('aiSummaryContextLevelType');
	}

	set aiSummaryContextLevelType(value: AiSummaryContextLevel | null) {
		this.setBuffered('aiSummaryContextLevelType', value);
	}

	get aiSummaryLengthType(): AiSummaryLength | null {
		return this.getBuffered('aiSummaryLengthType');
	}

	set aiSummaryLengthType(value: AiSummaryLength | null) {
		this.setBuffered('aiSummaryLengthType', value);
	}

	get aiSummaryEditedBy(): string | null | undefined {
		return this.getBuffered('aiSummaryEditedBy');
	}

	set aiSummaryEditedBy(value: string | null) {
		this.setBuffered('aiSummaryEditedBy', value);
	}

	get aiSummaryEditedOn(): number | null | undefined {
		return this.getBuffered('aiSummaryEditedOn');
	}

	set aiSummaryEditedOn(value: number | null) {
		this.setBuffered('aiSummaryEditedOn', value);
	}

	get aiSummaryStatus(): AiSummaryContentStatus {
		return this.getBuffered('aiSummaryStatus');
	}

	set aiSummaryStatus(value: AiSummaryContentStatus) {
		this.setBuffered('aiSummaryStatus', value);
	}

	// only on creating new block from metrickey
	// mapped from `fromMetric: {metricSetup, dateRange}`

	get fromMetricSetup(): DSSetup.MetricSetupData {
		return this.getBuffered('fromMetricSetup');
	}

	set fromMetricSetup(value: DSSetup.MetricSetupData) {
		this.setBuffered('fromMetricSetup', value);
	}

	get fromMetricDateRange(): DateRangeInterface.FormulaInterface | null {
		return this.getBuffered('fromMetricDateRange');
	}

	set fromMetricDateRange(value: DateRangeInterface.FormulaInterface | null) {
		this.setBuffered('fromMetricDateRange', value);
	}

	// previewHash & changes
	// only for preview-block (block-preview record attributes, so not typed on the buffer)
	get previewHash(): string {
		return this._buffer.getBuffered('previewHash') as string;
	}

	set previewHash(value: string) {
		this._buffer.setBuffered('previewHash', value);
	}

	get changes(): BlockPreviewRecord['changes'] {
		return this._buffer.getBuffered('changes') as BlockPreviewRecord['changes'];
	}

	set changes(value: BlockPreviewRecord['changes']) {
		this._buffer.setBuffered('changes', value);
	}

	// only for a preview block, whose record is a BlockPreviewRecord: sets its changes on the record directly
	directChangesUpdate(changes: BlockPreviewRecord['changes']): void {
		const props: DirectProps<BlockRecord | BlockPreviewRecord> = { changes };
		this.directPropUpdate(props);
	}

	get showTrafficLights(): boolean | null {
		return this.getBuffered('showTrafficLights');
	}

	set showTrafficLights(value: boolean | null) {
		this.setBuffered('showTrafficLights', value);
	}

	get hideTimeLeft(): boolean | null {
		return this.getBuffered('hideTimeLeft');
	}

	set hideTimeLeft(value: boolean | null) {
		this.setBuffered('hideTimeLeft', value);
	}

	// ------- other props ------

	get isImage(): boolean {
		return this.visualization === BlockVisualization.IMAGE;
	}

	get isText(): boolean {
		return this.visualization === BlockVisualization.TEXT;
	}

	get isPreview(): boolean {
		return !!this.previewHash;
	}

	get isAiSummary(): boolean {
		return this.visualization === BlockVisualization.AI_SUMMARY;
	}

	get isDataStory(): boolean {
		return this.visualization === BlockVisualization.DATA_STORY;
	}

	get isNumber(): boolean {
		return this.visualization === BlockVisualization.NUMBER;
	}

	get colorGradient() {
		let gradient = '';
		if (this.color) {
			gradient = getGradient(this.color, this.constantsService.boardColors);
		}
		return htmlSafe(gradient);
	}

	get yAxisScale1(): YAxisScale | undefined {
		return this.yAxisScale[0];
	}

	set yAxisScale1(value: YAxisScale) {
		const scaleFormulas = Array.from(this.yAxisScale); // copy
		scaleFormulas[0] = value;
		this.yAxisScale = scaleFormulas;
	}

	get yAxisScale2(): YAxisScale | undefined {
		return this.yAxisScale[1];
	}

	set yAxisScale2(value: YAxisScale) {
		const scaleFormulas = Array.from(this.yAxisScale); // copy
		scaleFormulas[1] = value;
		this.yAxisScale = scaleFormulas;
	}

	get shownText() {
		if (this.isAiSummary) {
			const fakeText =
				'<p>The sample data reflects subtle yet crucial movements across key performance indicators. Looking at <span class="metric">Sessions</span> metric, we see a slight drop from <span class="metric">102,203</span> to <span class="metric">101,199</span>, a <span class="negative">-1,004</span> change, which could suggest a stagnation in growth or potential challenges in user engagement. Meanwhile, the <span class="metric">Bounce Rate</span> has increased slightly, indicating that users might not be finding the content or experience as compelling as before.</p>';
			return this.isFeatureLimited ? fakeText : this.aiSummaryConfig.text;
		} else {
			return this.text;
		}
	}

	get showDataPerformanceSummary() {
		const board = this.board; // Board record (this.board) is returned for both previews and regular boards. Board model (this.boardObj) is not
		const report = this.reportSlideFragment?.report;

		return (
			this.navigationService.isMainApp &&
			!this.isText &&
			!this.isImage &&
			((board && !board.templateId && !board.isRandom) || (report && !report.isTemplate))
		);
	}

	get isOnTemplate(): boolean {
		return !!this.boardObj?.isTemplate || !!this.reportSlideFragment?.report?.isTemplate;
	}

	// ----------- limitation stuff -----------

	get limitationFeature() {
		return this.getLimitationForVisualization(this.visualization);
	}

	get isFeatureLimited() {
		const feature = this.limitationFeature;
		return (
			!this.boardObj?.isPublicTemplate &&
			!!feature &&
			!this.billingService.checkIfCan(feature)
		);
	}

	get showLimitationsOverlay() {
		// reports have their own overlay logic
		return this.isFeatureLimited && !this.isFromReport;
	}

	getLimitationForVisualization(visualization: BlockVisualization) {
		switch (visualization) {
			case BlockVisualization.DATA_STORY:
				// on reports, data-story is only on STORY_TEXT fragments, and there the whole slide will be checked / disabled
				return this.billingService.checkIfCan(BillingFeature.VISUALIZATIONS_ADVANCED)
					? null
					: BillingFeature.VISUALIZATIONS_ADVANCED;
			case BlockVisualization.AI_SUMMARY:
				return this.billingService.checkIfCan(BillingFeature.GENERATIVE_AI)
					? null
					: BillingFeature.GENERATIVE_AI;
			default:
				return null;
		}
	}

	// ----------- Datawall positioning stuff -----------

	get hasPositionOnWall(): boolean {
		return (
			typeof this.posX === 'number' &&
			typeof this.posY === 'number' &&
			!isNaN(this.posX) &&
			!isNaN(this.posY) &&
			!!this.width &&
			!!this.height
		);
	}

	get disableResize(): boolean {
		return this.model.isGenerating;
	}

	getPositioningProperties(inverse = false): GridPosition {
		// inverse if is public portrait datawall view
		if (inverse) {
			return {
				x: this.posY,
				y: this.posX,
				w: this.height,
				h: this.width,
			};
		} else {
			return {
				x: this.posX,
				y: this.posY,
				w: this.width,
				h: this.height,
			};
		}
	}

	// ----------- AI-summary stuff -----------

	// setting to null is required for the backend to know it has to regenerate the summary
	setAiSummaryEditInfoToNull() {
		this.aiSummaryEditedBy = null;
		this.aiSummaryEditedOn = null;
	}

	setAiSummaryStatusToLoading() {
		this.directPropUpdate({
			aiSummaryStatus: AiSummaryContentStatus.LOADING,
		});
	}

	@cached
	get aiSummaryConfig() {
		// ai-summary block specific, we have stringified json in config prop
		let config: { text?: string; trend?: DataPerformanceSummaryTrend } = {};
		try {
			config = JSON.parse(this.text) as typeof config;
		} catch (_e) {
			// skip
		}
		return config;
	}

	get aiSummaryTrend() {
		return this.aiSummaryConfig.trend || DataPerformanceSummaryTrend.UNAVAILABLE;
	}

	get shownAiSummaryTrend() {
		return this.isFeatureLimited ? DataPerformanceSummaryTrend.NEUTRAL : this.aiSummaryTrend;
	}

	get aiSummaryIsEdited() {
		return this.aiSummaryStatus === AiSummaryContentStatus.EDITABLE;
	}

	// ----------- blockSetup / dataSelect stuff -----------

	private _blockSetup: BlockSetup | null = null;

	get blockSetup(): BlockSetup {
		if (!this._blockSetup) {
			this._blockSetup = new BlockSetup(this);
		}
		return this._blockSetup;
	}

	get dataSelects() {
		return this.blockSetup.dataSelects;
	}

	get hasAnyNumberDataSelect() {
		return this.dataSelects.some((ds) => ds.limitations.isNumber);
	}

	getDSMapPlaceholderForDsId(dsId: number) {
		const dsMap = this.dsMap || {};
		const placeholder = Object.keys(dsMap).find((key) => {
			return dsId === dsMap[+key];
		});
		return placeholder || null;
	}

	// ----------- METRIC stuff -----------

	get metricDataArray() {
		return this.blockSetup.metricDataArray;
	}

	@cached
	get isEmpty() {
		return (
			this.visualization !== BlockVisualization.TEXT &&
			this.visualization !== BlockVisualization.IMAGE &&
			this.metricDataArray.length === 0
		);
	}

	// ----------- SOURCES stuff -----------

	@cached
	get missingSourceTypeIds(): number[] {
		const sourceTypeIds: number[] = [];
		this.blockSetup.dataSelectsShown.forEach((ds) => {
			const metric = ds.metricSetup.metric;
			if (metric && !metric.isOnSource) {
				const sourceTypeId = metric.sourceType?.id;
				if (sourceTypeId) {
					sourceTypeIds.push(sourceTypeId);
				}
			}
		});
		return uniq(sourceTypeIds);
	}

	get isAllUsedMetricsGeneric() {
		return !this.dataSelects.some((ds) => {
			return ds.metricSetupSpaceAccessId;
		});
	}

	replaceGenericMetrics(sourceTypeId: number, sourceIdString: string) {
		const blockSetup = this.blockSetup,
			sourceId = +sourceIdString;

		let compareChangePromises: Promise<unknown>[] = [],
			metricChangePromises: Promise<unknown>[] = [];

		// we want to keep selected attributes and units
		// temporarily disable autosave so the whole setup saves together
		blockSetup.isAutosaveOn = false;

		const isMetricFromGenericSourceTypeId = (metric: Metric | null) => {
			return metric && metric.isGeneric && metric.sourceType?.id === sourceTypeId;
		};

		const dsToReplace = blockSetup.dataSelectsShown.filter((ds) => {
			return isMetricFromGenericSourceTypeId(ds.metricSetup.metric);
		});

		metricChangePromises = dsToReplace.map((ds) => {
			const dimensions = ds.metricSetup.dimensions,
				filtersData = ds.metricSetup.filtersData,
				unitName = ds.metricSetup.unitName;
			return this.mmMetricsService
				.resolveGenericMetricWithSpecific(ds.metricSetup.key!, sourceId)
				.then((metricData) => {
					blockSetup.changeMetric(ds, metricData);
					blockSetup.changeDimensions(ds, dimensions);
					if (filtersData) {
						blockSetup.changeFilters(ds, filtersData);
					}
					if (typeof unitName === 'string') {
						blockSetup.changeUnit(ds, unitName);
					}
				});
		});

		const comparesWithOtherToReplace: { ds: DataSelect; idx: number }[] = [];
		blockSetup.dataSelectsShown.forEach((ds) => {
			ds.limitations.compareCMs.forEach((compare, idx) => {
				if (
					compare.isOtherMetric &&
					isMetricFromGenericSourceTypeId(compare.metricSetup.metric)
				) {
					comparesWithOtherToReplace.push({ ds, idx });
				}
			});
		});

		compareChangePromises = comparesWithOtherToReplace.map(({ ds, idx }) => {
			// we use index because we need to reference the exact compareCM from limitations when calling blockSetup.changeCompare
			const compare = ds.limitations.compareCMs[idx]!;
			return this.mmMetricsService
				.resolveGenericMetricWithSpecific(compare.metricSetup.key!, sourceId)
				.then((metricData) => {
					const newCompare = compare.createCopy();

					newCompare.metricSetup.setFromMetricSetupData({
						key: metricData.key,
						spaceAccessId: metricData.spaceAccessId,
						dimensions: compare.metricSetup.dimensions,
						filters: compare.metricSetup.filtersData,
					});

					blockSetup.changeCompare(ds, compare, newCompare);
				});
		});

		return Promise.all([...metricChangePromises, ...compareChangePromises]).then(() => {
			blockSetup.isAutosaveOn = true;
			void blockSetup.saveEverything();
			return blockSetup.afterSaves();
		});
	}

	// ----------- DATA stuff -----------

	get statusObjects(): StatusObject[] {
		return this.blockSetup.visualization.statusObjects;
	}

	get statusObjectsErrors(): StatusObject[] {
		return this.statusObjects.filter((item) => !item.isSync && !item.isMediaSync);
	}

	get statusObjectsSync(): StatusObject[] {
		return this.statusObjects.filter((so) => so.isSync || so.isMediaSync);
	}

	@cached
	get returnedData() {
		return this.sample?.sampledata || null;
	}

	@cached
	get dateRangeModel() {
		return DateRange.createFromFormula(this.dateRange);
	}

	@cached
	get isReturnedDataUpToDate(): boolean {
		// compares data range
		const dateRangeObjectOnData = this.returnedData?.dateRange,
			dateRangeModelOnBlock = this.dateRangeModel;

		if (dateRangeModelOnBlock.isEmpty) {
			// assume data is always up-to-date on fake blocks, e.g. forecast
			return true;
		}

		return (
			!!dateRangeObjectOnData &&
			dateRangeModelOnBlock &&
			(dateRangeModelOnBlock.isEqual(dateRangeObjectOnData) || this.isFromBlockPreview)
		);
	}

	@cached
	get data() {
		return this.isReturnedDataUpToDate ? this.returnedData : null;
	}

	@cached
	get dataTimezone() {
		return this.data?.timezone?.timezone || '';
	}

	@cached
	get lastSync() {
		const syncs = this.dataSelects.map((ds) => ds.lastSync || 0);
		return syncs.length ? Math.max(...syncs) : null;
	}

	@cached
	get isDataDifferentUnits() {
		const units: string[] = [];
		this.dataSelects.forEach((ds) => {
			ds.outputs.forEach((o) => {
				if (o.unit) {
					units.push(o.unit);
				}
			});
		});
		return uniq(units).length > 1;
	}

	@cached
	get isDataEmpty() {
		if (this.isAiSummary) {
			return !this.shownText;
		} else {
			return !this.data?.dsData.some((dataRoot) => {
				return (
					dataRoot.data.length > 0 ||
					dataRoot.compares?.some((compare) => {
						return compare.data.length > 0;
					})
				);
				// we don't check "others" bcos in theory, there cannot be "others" if we dont have any primaries
			});
		}
	}

	@cached
	get isDataIncomplete(): boolean {
		if (this.isText || this.isImage) {
			return false;
		} else {
			return !this.sample || this.isSyncing;
		}
	}

	discardSampleData() {
		const sampleRecord = this.sample;
		if (sampleRecord) {
			sampleRecord.unloadRecord();
		}
	}

	// ----------- VISUALIZATION stuff -----------

	get typeName(): string {
		return this.visualizationName;
	}

	get visualizationName(): string {
		return this.mmBlocksService.getVisualizationNameFromVisualizationType(this.visualization);
	}

	get switchableVisualizationList() {
		const mm = this.mmBlocksService;
		if (this.isFromReport) {
			return mm.switchableVisualizationListForReports;
		} else if (this.isFromCustomQuery) {
			return mm.switchableVisualizationListForQueryBuilder;
		} else if (this.isFromDataCalculation) {
			return mm.switchableVisualizationListForDataCalculations;
		} else if (this.isFromMetricInsight) {
			return mm.switchableVisualizationListForMetricDetails;
		} else {
			return mm.switchableVisualizationList;
		}
	}

	// ----------- BOARD stuff -----------

	@cached
	get board(): BoardRecord | BoardPreviewRecord | null {
		let board: BoardRecord | BoardPreviewRecord | null = null;
		if (this.boardId) {
			board = this.mmBoardsService.findRecord(String(this.boardId));
			// the snapshooter condition is a quick dirty fix to remove invalid warnings
			// bcos we dont know why daily snapshooter keeps throwing these errors
			// however, the snapshots seem to be done correctly, so... no idea.
			if (!board && !this.navigationService.isLoadedFromSnapshooter) {
				this.loggerService.logError(
					'Missing board for block',
					{
						blockId: this.id,
						boardId: this.boardId,
					},
					Severity.WARNING,
				);
			}
		} else if (this.previewHash) {
			board = this.mmBoardPreviewsService.findRecordByHash(this.previewHash);
		}
		return board || null;
	}

	@cached
	get boardObj(): Board | null {
		let boardModel;

		const board = this.board;
		if (board) {
			boardModel = this.mmBoardsService.findModel(board.id!);
			if (!boardModel && this.accountService.isPublictemplatesSpace) {
				boardModel = this.mmBoardsService.normalBoardModels.find(
					(item) => item.id === board?.id,
				)!;
			}
		}

		return boardModel || null;
	}

	// ----------- REPORT stuff -----------

	@cached
	get reportSlideFragment() {
		// in case this block is from Reports feature
		let fragment;
		if (!this.boardId) {
			const id = this.numericId;
			if (id) {
				const fragmentRecord = this.mmReportSlideFragmentsService.findForBlock(id);
				if (fragmentRecord) {
					fragment =
						this.mmReportSlideFragmentsService.createModelFromRecord(fragmentRecord);
				}
			}
		}
		return fragment;
	}

	get isFromReport() {
		return !!this.reportSlideFragment;
	}

	get isFromMetricInsight() {
		return this.metricDetailsService.isMetricInsightsTemporaryId(this.numericId?.toString());
	}

	get isFromGoalInsights() {
		return this.mmGoalsService.isTemporaryFakeId(this.id);
	}

	get isFromCustomQuery() {
		return this.mmCustomQueriesService.isTemporaryFakeId(this.id);
	}

	get isFromDataCalculation() {
		return this.mmScriptsService.isTemporaryFakeId(this.id);
	}

	get trackingOrigin() {
		let trackingOrigin = 'designer';

		if (this.isFromReport) {
			trackingOrigin = 'editor';
		} else if (this.isFromBlockPreview) {
			trackingOrigin = 'datablock_explorer';
		}

		return trackingOrigin;
	}

	get isFromReportBlockFragment() {
		const fragment = this.reportSlideFragment;
		return !!fragment && fragment.isTypeBlock;
	}

	get isFromReportStoryText() {
		const fragment = this.reportSlideFragment;
		return !!fragment && fragment.isTypeStoryText;
	}

	get isDataStoryOnSlide() {
		return (
			this.isDataStory &&
			this.reportSlideFragment &&
			!this.reportSlideFragment.isOnPagelessReport
		);
	}

	get isFromBlockPreview() {
		return this.discriminator === BlockDiscriminator.EXPLORER;
	}

	@cached
	get isShownInBlockPreview() {
		return this.isFromBlockPreview && this.popupService.isOpen(BlockPopupPreviewComponent);
	}

	@cached
	get sizes() {
		if (this.isShownInBlockPreview) {
			return {
				posX: 0,
				posY: 0,
				width: DatawallGrid.wallWidth,
				height: DatawallGrid.wallHeight,
			};
		}

		return { posX: this.posX, posY: this.posY, width: this.width, height: this.height };
	}

	get allowAdvancedSetup() {
		// allows multi dimensions and advanced filtering
		return (
			!!this.boardId ||
			!!this.previewHash ||
			this.isFromReport ||
			this.isFromBlockPreview ||
			this.isFromMetricInsight
		);
	}

	@cached
	get isAnyAdvancedSetupSelected() {
		return this.dataSelects.some((ds) => ds.metricSetup.isAdvancedSetupSelected);
	}

	// ----------- SOURCES stuff -----------

	@cached
	get sourcesObjs(): Source[] {
		const sources: Source[] = [];
		this.blockSetup.dataSelectsShown.forEach((ds) => {
			const source = ds.metricSetup.source;
			if (source && !sources.find((item) => item.id === source.id)) {
				sources.push(source);
			}
		});
		return sources;
	}

	get anyGenericSourcesChosen(): boolean {
		return this.missingSourceTypeIds.length > 0;
	}

	// ----------- DATA stuff -----------

	get sample(): SampleRecord | null {
		const id = this.id;
		if (id) {
			return this.mmSamplesService.findRecord(id) || null;
		}
		return null;
	}

	@cached
	get isSyncing() {
		return this.statusObjects.some((so) => so.isSync);
	}

	// ----------- Static stuff -----------

	static override modelName = ModelName.BLOCK; // store record type
}
